interface LocationOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
}

class LocationService {
  private watchId: number | null = null;
  private currentLocation: GeolocationPosition | null = null;
  private locationCallbacks: ((location: GeolocationPosition) => void)[] = [];

  async requestPermission(): Promise<boolean> {
    if (!navigator.geolocation) {
      console.error('Geolocation is not supported by this browser');
      return false;
    }

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        () => resolve(true),
        () => resolve(false),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    });
  }

  async getCurrentLocation(options: LocationOptions = {}): Promise<GeolocationPosition> {
    const defaultOptions = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 60000,
      ...options,
    };

    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.currentLocation = position;
          resolve(position);
        },
        (error) => {
          console.error('Error getting current location:', error);
          reject(error);
        },
        defaultOptions
      );
    });
  }

  startLocationTracking(callback: (location: GeolocationPosition) => void, options: LocationOptions = {}): number {
    const defaultOptions = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 3000,
      ...options,
    };

    this.locationCallbacks.push(callback);

    this.watchId = navigator.geolocation.watchPosition(
      (position) => {
        this.currentLocation = position;
        this.locationCallbacks.forEach(cb => cb(position));
      },
      (error) => {
        console.error('Error in location tracking:', error);
      },
      defaultOptions
    );

    return this.watchId;
  }

  stopLocationTracking(): void {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
    this.locationCallbacks = [];
  }

  getCurrentLocationData(): GeolocationPosition | null {
    return this.currentLocation;
  }

  isLocationAvailable(): boolean {
    return this.currentLocation !== null;
  }

  calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }
}

export const locationService = new LocationService(); 